import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import siteConfig from "../config/siteConfig";
import "../css/about.scss";

function About() {
  const location = useLocation();
  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  useEffect(() => {
    if (!location.hash) return undefined;

    const frame = window.requestAnimationFrame(() => {
      document.getElementById(location.hash.slice(1))?.scrollIntoView({
        block: "start",
      });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [location.hash]);

  return (
    <main className="about-page">
      <section id="history" className="about-hero" aria-labelledby="about-title">
        <div className="about-hero__copy">
          <p className="about-kicker">ABOUT / {siteConfig.brandName}</p>
          <h1 id="about-title">Everyday layers. Your own way.</h1>
          <p className="about-hero__intro">{siteConfig.description}</p>
        </div>

        <div className="about-hero__stamp" aria-hidden="true">
          <span>{siteConfig.origin}</span>
          <strong>S</strong>
          <span>{siteConfig.tagline}</span>
        </div>
      </section>

      <section className="about-facts" aria-label="Our foundations">
        <div>
          <strong>SC</strong>
          <span>Everyday essentials</span>
        </div>
        <div>
          <strong>01</strong>
          <span>{siteConfig.origin}</span>
        </div>
        <div>
          <strong>Built</strong>
          <span>To outlast trends</span>
        </div>
      </section>

      <section id="factory" className="about-process" aria-labelledby="process-title">
        <header className="about-section-heading">
          <p className="about-kicker">OUR APPROACH</p>
          <h2 id="process-title">Considered for everyday life.</h2>
        </header>

        <div className="about-process__grid">
          <article>
            <span>01</span>
            <h3>Texture</h3>
            <p>
              Explore substantial knits, textured cotton and woven layers,
              with materials described on each product page.
            </p>
          </article>
          <article>
            <span>02</span>
            <h3>Comfort</h3>
            <p>
              Compare fits and fabric weights to find the right balance
              of structure, softness and room to move.
            </p>
          </article>
          <article>
            <span>03</span>
            <h3>Details</h3>
            <p>
              Pockets, collars and everyday proportions give each piece
              its character. Consult the item details before choosing.
            </p>
          </article>
          <article>
            <span>04</span>
            <h3>Care</h3>
            <p>
              Follow the care label supplied with your garment to help
              keep its texture, colour and shape in good condition.
            </p>
          </article>
        </div>
      </section>

      <section className="about-standard" aria-labelledby="standard-title">
        <div className="about-standard__graphic" aria-hidden="true">
          <span>KNIT</span>
          <span>CUT</span>
          <span>SEW</span>
          <span>FINISH</span>
        </div>
        <div className="about-standard__copy">
          <p className="about-kicker">OUR STANDARD</p>
          <h2 id="standard-title">Useful by design. Better with age.</h2>
          <p>
            {siteConfig.brandName} is grounded in utility: dependable layers,
            familiar silhouettes and materials that reveal more character the
            longer they are worn.
          </p>
          <blockquote>
            “Find your comfort. Make it your own.”
          </blockquote>
        </div>
      </section>

      <section id="retail-store" className="about-retail" aria-labelledby="retail-title">
        <div>
          <p className="about-kicker">OUR COMPANY / {siteConfig.origin}</p>
          <h2 id="retail-title">Meet suddlecorner.</h2>
        </div>
        <div className="about-retail__details">
          <p>
            {siteConfig.brandName} is operated by {siteConfig.companyName}.
            For product and order questions, contact our team by email.
          </p>
          {siteConfig.contact?.address && <address>{siteConfig.contact.address}</address>}
          <a href={`mailto:${siteConfig.contact.email}`}>{siteConfig.contact.email}</a>
        </div>
      </section>

      <section className="about-cta" aria-labelledby="about-cta-title">
        <p className="about-kicker">KEEP IT IN ROTATION</p>
        <h2 id="about-cta-title">Find your next everyday layer.</h2>
        <div className="about-cta__links">
          <Link to="/shop" onClick={scrollToTop}>
            Shop the collection
          </Link>
          <Link to="/contact" onClick={scrollToTop}>
            Talk to us
          </Link>
        </div>
      </section>
    </main>
  );
}

export default About;
